import { Box, Button, Divider, Grid, Switch, Typography, useTheme } from '@mui/material';
import { FieldArray, useFormikContext } from 'formik';
import { Add, Trash } from 'iconsax-react';
import { DateTime } from 'luxon';
import { type ChangeEvent, type ReactElement, useEffect, useMemo, useState } from 'react';

import type { QuoteFormikType } from '@core/formik/quote';
import Enums from '@core/types/api/Enums';
import { getCurrencySymbol } from '@core/utils/format';
import { PriceEscalationInput } from '@web/components/PriceEscalationInput/PriceEscalationInput';
import { EMPTY_LINE_CHAR } from '@web/constants/app';
import { useIntegrations } from '@web/hooks/useIntegrations';
import { useSlabQuery } from '@web/hooks/useSlabQuery';

import { useQuoteDrawerContext } from '../QuoteDrawer';

type SysdyneConfig = {
  Plants?: Array<{
    ID: string;
    SlabstackID: string;
    LocationCode: string;
  }>;
};

export const newEmptyPriceEscalation: QuoteFormikType['priceEscalations'][number] = {
  changeRatio: null,
  escalationDate: DateTime.now().plus({ year: 1 }).startOf('year').toISO(),
  flatChange: null,
  kind: Enums.EscalationKind.Percentage,
  appliedAt: null,
  appliedBy: null,
  isProcessed: false,
};

export const PriceEscalationSection = (): ReactElement => {
  const { readOnly } = useQuoteDrawerContext();
  const theme = useTheme();
  const formikBag = useFormikContext<QuoteFormikType>();
  const { values } = formikBag;
  const { integrations, isLoading } = useIntegrations();

  const usedPlantIds = useMemo(
    () => values.mainProductsByPlants?.map((item) => item.plant?.id).filter(Boolean) ?? [],
    [values.mainProductsByPlants],
  );

  // Flat escalation amounts use the currency of the quote's first plant.
  // Kept in sync as the plant selection changes.
  const firstPlantID = usedPlantIds[0] ?? null;
  const { data: maybeFirstPlant } = useSlabQuery(
    'GET plant by ID',
    { pathParams: { id: firstPlantID ?? '' } },
    { enabled: firstPlantID !== null },
  );
  const currencySymbol = getCurrencySymbol(
    firstPlantID === null ? null : maybeFirstPlant?.currencyCode,
  );

  const isSysdyneQuote = useMemo(() => {
    if (isLoading || !integrations?.length || !usedPlantIds.length) return false;

    return integrations.some(
      (i) =>
        i.integrationType === Enums.IntegrationType.Sysdyne &&
        i.isActive &&
        !i.isArchived &&
        (i.config as SysdyneConfig)?.Plants?.some(
          (p) => p.SlabstackID && usedPlantIds.includes(p.SlabstackID),
        ),
    );
  }, [integrations, isLoading, usedPlantIds]);

  const [globalKind, setGlobalKind] = useState<Enums.EscalationKind>(
    Enums.EscalationKind.Percentage,
  );

  useEffect(() => {
    if (formikBag.values.priceEscalations.length > 0) {
      setGlobalKind(formikBag.values.priceEscalations[0].kind as Enums.EscalationKind);
    }
  }, [isLoading]);

  const onEscalationSwitchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const isChecked = event.target.checked;
    const newKind = isChecked ? Enums.EscalationKind.Flat : Enums.EscalationKind.Percentage;
    setGlobalKind(newKind);

    const updatedEscalations = formikBag.values.priceEscalations.map((item) => ({
      ...item,
      kind: newKind,
    }));
    formikBag.setFieldValue('priceEscalations', updatedEscalations);
  };

  return (
    <Box>
      <Typography color={theme.palette.error.main}>
        {typeof formikBag.errors.priceEscalations === 'string'
          ? formikBag.errors.priceEscalations
          : EMPTY_LINE_CHAR}
      </Typography>
      {isSysdyneQuote && (
        <Box mb={2}>
          <Typography variant='subtitle2' gutterBottom>
            Global Escalation Setting
          </Typography>
          <Grid component='label' container alignItems='center' spacing={1}>
            <Grid item>Percentage Change</Grid>
            <Grid item>
              <Switch
                checked={globalKind === Enums.EscalationKind.Flat}
                onChange={onEscalationSwitchChange}
                disabled={readOnly}
                color='primary'
              />
            </Grid>
            <Grid item>Flat Amount</Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
        </Box>
      )}

      <FieldArray
        name='priceEscalations'
        // Sees 2x performance benefit at no behavior compromise.
        validateOnChange={false}
        render={(arrayHelpers): ReactElement => (
          <>
            {formikBag.values.priceEscalations.map((_, index) => (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={`quote-price-escalation-${formikBag.values.id}-${index}`}
                paddingY='1.25rem'
                display='flex'
                flexDirection='column'
                gap='1rem'
              >
                <Grid container spacing={2}>
                  <Grid item xs={11}>
                    <PriceEscalationInput
                      readOnly={readOnly}
                      name={`priceEscalations.${index}`}
                      currencySymbol={currencySymbol}
                      enforcedKind={isSysdyneQuote ? globalKind : undefined}
                    />
                  </Grid>
                  {!readOnly && (
                    <Grid item display='flex' justifyContent='center' alignItems='center' xs={1}>
                      <Button
                        onClick={(): void => {
                          arrayHelpers.remove(index);
                        }}
                      >
                        <Trash variant='TwoTone' color={theme.palette.SlabIndigo['700']} />
                      </Button>
                    </Grid>
                  )}
                </Grid>
                <Divider />
              </Box>
            ))}
            {!readOnly && (
              <Button
                onClick={(): void => {
                  arrayHelpers.push({
                    ...newEmptyPriceEscalation,
                    kind: isSysdyneQuote ? globalKind : Enums.EscalationKind.Percentage,
                  });
                }}
                sx={{
                  textTransform: 'none',
                  color: theme.palette.SlabIndigo['700'],
                }}
              >
                <Box display='flex' gap='0.5rem'>
                  <Add color={theme.palette.SlabIndigo['700']} />
                  Add{' '}
                  {formikBag.values.priceEscalations.length === 0 ? 'price escalation' : 'another'}
                </Box>
              </Button>
            )}
          </>
        )}
      />
    </Box>
  );
};
